import React from "react";
import { Link } from "@/lib/router-compat";
import { services } from "../data/services";
import { serviceAreaCities } from "../data/serviceAreas";

type Phrase = { text: string; href: string };

const phrases: Phrase[] = [
  ...services.map((service) => ({
    text: service.name,
    href: `/services/${service.slug}`,
  })),
  { text: "seasonal cleanup", href: "/services/spring-cleanup" },
  { text: "dumpsters", href: "/services/dumpster-rental" },
  { text: "weeds", href: "/services/weed-control" },
  { text: "clearing", href: "/services/land-clearing" },
  { text: "cleanup", href: "/services/spring-cleanup" },
  ...serviceAreaCities.map((city) => ({
    text: city.name,
    href: `/service-areas/${city.slug}`,
  })),
].sort((a, b) => b.text.length - a.text.length);

const isWordChar = (char: string | undefined) =>
  Boolean(char && /[A-Za-z0-9]/.test(char));

export const LinkedText: React.FC<{ text: string; skipHref?: string }> = ({
  text,
  skipHref,
}) => {
  const lower = text.toLowerCase();
  const taken: { start: number; end: number }[] = [];
  const links: { start: number; end: number; href: string }[] = [];
  const usedHrefs = new Set<string>();

  for (const phrase of phrases) {
    const needle = phrase.text.toLowerCase();
    let from = 0;
    while (from < lower.length) {
      const start = lower.indexOf(needle, from);
      if (start < 0) break;
      const end = start + needle.length;
      const before = text[start - 1];
      const after = text[end];
      const prefix = lower.slice(Math.max(0, start - 12), start);
      const blockedPrefix =
        (needle === "cleanup" && /(spring |fall |seasonal )$/.test(prefix)) ||
        (needle === "clearing" && /land $/.test(prefix)) ||
        (needle === "highland" && lower.slice(end, end + 5) === " city");
      const overlaps = taken.some(
        (span) => start < span.end && end > span.start,
      );
      if (
        !isWordChar(before) &&
        !isWordChar(after) &&
        !blockedPrefix &&
        !overlaps
      ) {
        taken.push({ start, end });
        if (phrase.href !== skipHref && !usedHrefs.has(phrase.href)) {
          links.push({ start, end, href: phrase.href });
          usedHrefs.add(phrase.href);
        }
        break;
      }
      from = start + 1;
    }
  }

  links.sort((a, b) => a.start - b.start);
  const nodes: React.ReactNode[] = [];
  let cursor = 0;
  links.forEach((link, index) => {
    if (link.start > cursor) nodes.push(text.slice(cursor, link.start));
    nodes.push(
      <Link
        key={`${link.href}-${index}`}
        to={link.href}
        className="font-semibold text-[#D96B27] underline decoration-[#D96B27]/40 underline-offset-2 hover:decoration-[#D96B27]"
      >
        {text.slice(link.start, link.end)}
      </Link>,
    );
    cursor = link.end;
  });
  if (cursor < text.length) nodes.push(text.slice(cursor));
  return <>{nodes}</>;
};
